import {
    Box,
    Button,
    CardActions,
    CardContent,
    Checkbox,
    CircularProgress,
    Container,
    Drawer,
    FormControl,
    FormControlLabel,
    FormGroup,
    FormHelperText,
    FormLabel,
    Grid,
    InputLabel,
    TextField,
    Typography
} from '@mui/material';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { apiService } from '../../api/axiosClient';
import { InputCurrencyMask } from '../../components/input-numbers';
import CustomChip from '../../components/mui/chip';
import FileUploaderSingle from '../../components/upload/FileUploaderSingle';
import DropzoneWrapper from '../../styles/react-dropzone';

const DialogProduct = (props) => {
    const { productID, show, setShow, setReload } = props
    const [loading, setLoading] = useState(false)
    const [product, setProduct] = useState(null);
    const [files, setFiles] = useState([]);
    const {
        control,
        handleSubmit,
        formState: { errors },
        reset,
        setValue,
        watch
    } = useForm()

    const defaultValues = {
        sku: product?.sku,
        name: product?.name,
        keywords: product?.keywords,
        category: product?.category1 + ' > ' + product?.category2 + ' > ' + product?.category3,
        brand: product?.brand,
        price: product?.price,
        sale_price: product?.sale_price,
        stock_kor: product?.stock_kor,
        stock_intl: product?.stock_intl,
        brandnew: product?.attrib?.includes('brandnew') || false,
        best: product?.attrib?.includes('best') || false,
        sale: product?.attrib?.includes('sale') || false,
        it_use: product?.it_use,
        soldout: product?.it_soldout
    }

    const getProduct = (id) => {
        const fetchData = async () => {
            try {
                const response = await apiService.get(`/products/${id}`);
                const payload = response.data;
                setProduct(payload.data);
            } catch (error) {
                console.error(error);
            }
        }

        fetchData();
    }

    useEffect(() => {
        const subscription = watch((value, { name }) => {
            if(name == 'soldout') {
                value.soldout ? setValue('stock', '0') : setValue('stock', '10');
            }
        })
        return () => subscription.unsubscribe()
    }, [watch])

    useEffect(() => {
        if(show) {
            productID ? getProduct(productID) : setProduct(null);      
        } else {
            reset(defaultValues);
        }
    }, [show])

    useEffect(() => {
        reset(defaultValues);
    }, [product]);

    useEffect(() => {
        setFiles(product?.icon1 ? [`https://aladdinsci-bucket.s3.ap-northeast-2.amazonaws.com/${product?.icon1}`] : [])
    }, [product?.icon1]);

    const changeProduct = async (params) => {
        try {
            await apiService.post(`/products/${product.sku}`, params, {
                headers: { "Content-Type": "multipart/form-data" }
            });
            toast.success('수정 되었습니다');
            setReload(reload => reload + 1);
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
            setShow(false);
        }
    }

    const onSubmit = async (data, e) => {
        const [file] = files;
        const formData = new FormData()
        const attrib = []

        if (data.brandnew) attrib.push('brandnew')
        if (data.best) attrib.push('best')
        if (data.sale) attrib.push('sale')

        const attr = attrib?.length > 0 ? attrib.join(",") : '';

        formData.append('name', data.name);
        formData.append('keywords', data.keywords);
        formData.append('brand', data.brand);
        formData.append('price', data.price);
        formData.append('sale_price', data.sale_price);
        formData.append('stock', data.stock);
        formData.append('attrib', attr);
        formData.append('it_use', data.it_use);
        formData.append('soldout', data.soldout);
        formData.append('file', file);

        setLoading(true)
        changeProduct(formData)
    }

    const toggleDrawer = (open) => (event) => {
        if (event.type === 'keydown' && (event.key === 'Tab' || event.key === 'Shift')) {
            return;
        }
        setShow(open);
    };

    return (
        <Drawer anchor='right' open={show} onClose={toggleDrawer(false)}>
            <Container sx={{width: '800px', pt: 8, px: '2rem !important'}}>
                <form onSubmit={handleSubmit(onSubmit)} id="productform">
                    <CardContent sx={{ pt: 8 }}>
                        <Box sx={{ mb: 9, textAlign: 'center' }}>
                            <Typography variant='h5' sx={{ mb: 3, lineHeight: '2rem' }}>
                                제품 정보
                            </Typography>
                        </Box>

                        <Grid container spacing={6}>
                            <Grid item xs={12} md={6}>
                                <FormControl fullWidth>
                                    <Controller
                                        name='sku'
                                        control={control}
                                        defaultValue=""
                                        render={({ field: { value, onChange } }) => (
                                            <TextField
                                                value={value || ''}
                                                disabled
                                                label='SKU'
                                                id='sku'
                                                onChange={onChange}
                                                placeholder='이름을 입력해주세요'
                                                error={Boolean(errors.sku)}
                                                aria-describedby='validation-async-sku'
                                            />
                                        )}
                                    />
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <FormControl fullWidth>
                                    <Controller
                                        name='category'
                                        control={control}
                                        defaultValue=""
                                        render={({ field: { value, onChange } }) => (
                                            <TextField
                                                value={value || ''}
                                                disabled
                                                label='카테고리'
                                                id='category'
                                                onChange={onChange}
                                                placeholder='이름을 입력해주세요'
                                                error={Boolean(errors.category)}
                                                aria-describedby='validation-async-category'
                                            />
                                        )}
                                    />
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <FormControl fullWidth>
                                    <Controller
                                        name='name'
                                        control={control}
                                        rules={{ required: true }}
                                        defaultValue=""
                                        render={({ field: { value, onChange } }) => (
                                            <TextField
                                                value={value || ''}
                                                label='이름'
                                                id='name'
                                                onChange={onChange}
                                                placeholder='이름을 입력해주세요'
                                                error={Boolean(errors.name)}
                                                aria-describedby='validation-async-name'
                                            />
                                        )}
                                    />
                                    {errors.name && (
                                        <FormHelperText sx={{ color: 'error.main' }} id='validation-async-name'>
                                            필수
                                        </FormHelperText>
                                    )}
                                </FormControl>
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <FormControl fullWidth>
                                    <Controller
                                        name='brand'
                                        control={control}
                                        rules={{ required: true }}
                                        defaultValue=""
                                        render={({ field: { value, onChange } }) => (
                                            <TextField
                                                value={value || ''}
                                                label='브랜드'
                                                id='brand'
                                                onChange={onChange}
                                                placeholder='브랜드를 입력해주세요'
                                                error={Boolean(errors.brand)}
                                                aria-describedby='validation-async-brand'
                                            />
                                        )}
                                    />
                                    {errors.brand && (
                                        <FormHelperText sx={{ color: 'error.main' }} id='validation-async-brand'>
                                            필수
                                        </FormHelperText>
                                    )}
                                </FormControl>
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <FormControl fullWidth>
                                    <InputLabel htmlFor="price">단가</InputLabel>
                                    <Controller
                                        name='price'
                                        control={control}
                                        rules={{ required: true }}
                                        defaultValue=""
                                        render={({ field: { value, onChange } }) => (
                                            <InputCurrencyMask                                                
                                                value={value || ''}
                                                label='단가'
                                                id='price'
                                                onChange={onChange}
                                                placeholder='단가를 입력해주세요'
                                                error={Boolean(errors.price)}
                                            />
                                        )}
                                    />
                                    {errors.price && (
                                        <FormHelperText sx={{ color: 'error.main' }} id='validation-async-price'>
                                            필수
                                        </FormHelperText>
                                    )}
                                </FormControl>
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <FormControl fullWidth>
                                    <InputLabel htmlFor="price">판매가</InputLabel>
                                    <Controller
                                        name='sale_price'
                                        control={control}
                                        rules={{ required: true }}
                                        defaultValue=""
                                        render={({ field: { value, onChange } }) => (
                                            <InputCurrencyMask
                                                value={value || ''}
                                                label='판매가'
                                                id='sale_price'
                                                onChange={onChange}
                                                placeholder='판매가를 입력해주세요'
                                                error={Boolean(errors.sale_price)}
                                                aria-describedby='validation-async-sale_price'
                                            />
                                        )}
                                    />
                                    {errors.sale_price && (
                                        <FormHelperText sx={{ color: 'error.main' }} id='validation-async-sale_price'>
                                            필수
                                        </FormHelperText>
                                    )}
                                </FormControl>
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <FormControl fullWidth>
                                    <Controller
                                        name='stock_kor'
                                        control={control}
                                        rules={{ required: true }}
                                        defaultValue=""
                                        render={({ field: { value, onChange } }) => (
                                            <TextField
                                                value={value || ''}
                                                label='국내 재고수량'
                                                id='stock_kor'
                                                onChange={onChange}
                                                placeholder='재고수량를 입력해주세요'
                                                error={Boolean(errors.stock_kor)}
                                                aria-describedby='validation-async-stock_kor'
                                            />
                                        )}
                                    />
                                    {errors.stock_kor && (
                                        <FormHelperText sx={{ color: 'error.main' }} id='validation-async-stock_kor'>
                                            필수
                                        </FormHelperText>
                                    )}
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <FormControl fullWidth>
                                    <Controller
                                        name='stock_intl'
                                        control={control}
                                        rules={{ required: true }}
                                        defaultValue=""
                                        render={({ field: { value, onChange } }) => (
                                            <TextField
                                                value={value || ''}
                                                label='해외 재고수량'
                                                id='stock_intl'
                                                onChange={onChange}
                                                placeholder='재고수량를 입력해주세요'
                                                error={Boolean(errors.stock_intl)}
                                                aria-describedby='validation-async-stock_intl'
                                            />
                                        )}
                                    />
                                    {errors.stock_intl && (
                                        <FormHelperText sx={{ color: 'error.main' }} id='validation-async-stock_intl'>
                                            필수
                                        </FormHelperText>
                                    )}
                                </FormControl>
                            </Grid>

                            <Grid item xs={12}>
                                <FormControl fullWidth>
                                <Controller
                                    name='keywords'
                                    control={control}
                                    rules={{ required: true }}
                                    defaultValue=""
                                    render={({ field: { value, onChange } }) => (
                                    <TextField
                                        value={value || ''}
                                        label='태그'
                                        id='keywords'
                                        onChange={onChange}
                                        placeholder='짭은 이름을 입력해주세요'
                                        error={Boolean(errors.reply)}
                                        aria-describedby='validation-async-keywords'
                                    />
                                    )}
                                />
                                {errors.keywords && (
                                    <FormHelperText sx={{ color: 'error.main' }} id='validation-async-keywords'>
                                    필수
                                    </FormHelperText>
                                )}
                                </FormControl>
                            </Grid>

                            <Grid item xs={6}>
                                <FormControl>
                                    <FormLabel>상품속성</FormLabel>
                                    <FormGroup row>
                                        <Controller
                                            name='brandnew'
                                            control={control}
                                            rules={{ required: false }}
                                            defaultValue={false}
                                            render={({ field: { value, onChange } }) => (
                                                <FormControlLabel
                                                    label='신상품'
                                                    control={
                                                        <Checkbox checked={!!value} onChange={onChange} name='brandnew' />
                                                    }
                                                />
                                            )}
                                        />
                                        <Controller
                                            name='best'
                                            control={control}
                                            rules={{ required: false }}
                                            defaultValue={false}
                                            render={({ field: { value, onChange } }) => (
                                                <FormControlLabel
                                                    label='베스트'
                                                    control={
                                                        <Checkbox checked={!!value} onChange={onChange} name='best' />
                                                    }
                                                />
                                            )}
                                        />
                                        <Controller
                                            name='sale'
                                            control={control}
                                            rules={{ required: false }}
                                            defaultValue={false}
                                            render={({ field: { value, onChange } }) => (
                                                <FormControlLabel
                                                    label='세일'
                                                    control={
                                                        <Checkbox checked={!!value} onChange={onChange} name='sale' />
                                                    }
                                                />
                                            )}
                                        />
                                    </FormGroup>
                                </FormControl>
                            </Grid>

                            <Grid item xs={6}>
                                <FormControl>
                                    <FormLabel>상품표시</FormLabel>
                                    <FormGroup row>
                                        <Controller
                                            name='it_soldout'
                                            control={control}
                                            rules={{ required: false }}
                                            defaultValue={false}
                                            render={({ field: { value, onChange } }) => (
                                                <FormControlLabel
                                                    label='SOLDOUT'
                                                    control={
                                                        <Checkbox checked={!!value} onChange={onChange} name='it_soldout' />
                                                    }
                                                />
                                            )}
                                        />
                                        <Controller
                                            name='it_use'
                                            control={control}
                                            rules={{ required: false }}
                                            defaultValue={false}
                                            render={({ field: { value, onChange } }) => (
                                                <FormControlLabel
                                                    label='상품노출'
                                                    control={
                                                        <Checkbox checked={!!value} onChange={onChange} name='it_use' />
                                                    }
                                                />
                                            )}
                                        />
                                    </FormGroup>
                                </FormControl>
                            </Grid>

                            <Grid item xs={12}>
                                <DropzoneWrapper>
                                    <FileUploaderSingle files={files} setFiles={setFiles} update={true} />
                                </DropzoneWrapper>
                            </Grid>

                            <Grid item xs={12}>
                                <CustomChip
                                    skin='light'
                                    color='info'
                                    label={`등록날짜: ${product?.insert_time}`}
                                    sx={{ '& .MuiChip-label': { color: 'common.black' } }}
                                />
                            </Grid>
                        </Grid>
                    </CardContent>
                </form>

                <CardActions className='card-action'>
                    <Button type="submit" form="productform" variant='contained'>
                        { loading ? (
                            <CircularProgress
                                sx={{
                                color: 'common.white',
                                width: '20px !important',
                                height: '20px !important',
                                mr: theme => theme.spacing(2)
                                }}
                            />
                            ) : null
                        }
                        수정 
                    </Button>
                    <Button variant='outlined' color='secondary' onClick={() => setShow(false)}> 취소 </Button>
                </CardActions>
            </Container>
        </Drawer>
    )
}

export default DialogProduct